/**
 * Seller CMA Chat Service
 * 
 * KOPS-384: Created on February 16, 2026
 * 
 * Purpose: Handle AI-powered conversations about property valuations for sellers
 * - Answers questions about XGBoost valuations
 * - Explains comparable properties and market analysis
 * - Provides market timing insights
 * - Offers pricing strategy recommendations
 * - Handles seller-specific concerns and objections
 */

import axiosInstance from '../helpers/api';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface CMAData {
  property: {
    address: string;
    bedrooms: number;
    bathrooms: number;
    living_area: number;
    year_built: number;
    property_type: string;
  };
  valuation: {
    estimated_value: number;
    confidence_score: number;
    price_range: {
      min: number;
      max: number;
    };
    methodology: string;
    features_used: number;
    features_total: number;
  };
  comparables?: any[];
  comparable_count: number;
  market_analysis: {
    analysis_type: string;
    search_radius: number;
    generated_at: string;
  };
  market_stats?: {
    average_sale_price?: number;
    median_sale_price?: number;
    average_days_on_market?: number;
    recent_sales_count?: number;
    price_trend?: string;
  };
}

class SellerCMAConsultativeService {
  private sessionId: string;
  private leadId: string | null = null;
  private conversationHistory: ChatMessage[] = [];
  private cmaData: CMAData | null = null;
  
  constructor() {
    this.sessionId = this.generateSessionId();
  }
  
  private generateSessionId(): string {
    return 'cma_session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
  
  getSessionId(): string {
    return this.sessionId;
  }
  
  setLeadId(leadId: string): void {
    this.leadId = leadId;
  }
  
  setCMAData(cmaData: CMAData): void {
    this.cmaData = cmaData;
  }
  
  async fetchComparables(reportId: string, options?: { 
    radius?: number; 
    months?: number; 
    type?: 'all' | 'renovated' | 'as-is' 
  }): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (options?.radius) params.append('radius', options.radius.toString());
      if (options?.months) params.append('months', options.months.toString());
      
      let endpoint = `/public/cma-report/${reportId}/comps`;
      if (options?.type && options.type !== 'all') {
        endpoint += '/search';
        params.append('type', options.type);
        if (options.type === 'renovated') {
          params.append('keywords', 'updated,renovated,remodeled,new');
        }
      }
      
      const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching comparables:', error);
      throw error;
    }
  }
  
  async sendMessage(userMessage: string): Promise<any> {
    try {
      // Add user message to history
      this.conversationHistory.push({ role: 'user', content: userMessage });
      
      // Build context with CMA data
      const systemPrompt = this.buildSystemPrompt();
      
      const response = await axiosInstance.post('/public/ai/chat', {
        messages: [
          { role: 'system', content: systemPrompt },
          ...this.conversationHistory
        ],
        sessionId: this.sessionId,
        leadId: this.leadId,
        context: {
          type: 'seller_cma',
          cmaData: this.cmaData
        }
      });
      
      // Add assistant response to history
      if (response.data.message) {
        this.conversationHistory.push({ 
          role: 'assistant', 
          content: response.data.message 
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error in CMA chat:', error);
      throw error;
    }
  }
  
  private buildSystemPrompt(): string {
    if (!this.cmaData) {
      return `You are a helpful real estate AI assistant. The user is viewing a CMA (Comparative Market Analysis) report but the data hasn't loaded yet. Be helpful and encouraging.`;
    }
    
    const { property, valuation, comparable_count, market_stats } = this.cmaData;
    
    const priceRangeLow = valuation.price_range.min;
    const priceRangeHigh = valuation.price_range.max;

    // Build comp details for context
    const compDetails = (this.cmaData.comparables || []).map((c: any, i: number) =>
      `- ${c.address}: Sold $${(c.close_price || 0).toLocaleString()}, ${c.bedrooms || 0}bd/${c.bathrooms || 0}ba, ${(c.living_area || 0).toLocaleString()}sqft, $${c.price_per_sqft || Math.round((c.close_price || 0) / (c.living_area || 1))}/sqft, ${c.classification === 'renovated' ? 'Updated/Renovated' : 'Original condition'}, ${c.days_on_market || 0} days on market`
    ).join('\n');

    return `You are a helpful real estate assistant working on behalf of the seller's real estate agent. You are helping a homeowner review their property's market analysis report.

PROPERTY:
- ${property.address}
- ${property.bedrooms} bed / ${property.bathrooms} bath, ${property.living_area.toLocaleString()} sqft, built ${property.year_built}

MARKET ANALYSIS:
- Estimated market value range: $${priceRangeLow.toLocaleString()} - $${priceRangeHigh.toLocaleString()}
- Based on ${comparable_count} recent comparable sales in the area

COMPARABLE SALES (you have full access to this data - reference specific addresses and prices):
${compDetails || 'No detailed comp data available'}
${market_stats ? `- Median area sale price: $${market_stats.median_sale_price?.toLocaleString() || 'N/A'}
- Average days on market: ${market_stats.average_days_on_market || 'N/A'} days
- Market trend: ${market_stats.price_trend || 'Stable'}` : ''}

IMPORTANT RULES - YOU MUST FOLLOW THESE:
1. NEVER give a specific listing price recommendation. Give the RANGE ($${Math.round(priceRangeLow/1000)}K-$${Math.round(priceRangeHigh/1000)}K) and say "your agent can help determine the ideal listing price."
2. NEVER mention AI models, XGBoost, machine learning, confidence scores, or prediction accuracy. Say "market analysis" or "comparable sales" instead.
3. ALWAYS position the agent as the expert for pricing decisions.
4. ALWAYS encourage the seller to discuss strategy with their agent.
5. When asked about comps, REFERENCE SPECIFIC ADDRESSES AND PRICES from the comparable sales data above. You HAVE this data - use it. Say "For example, a similar home at [address] recently sold for $[price]..."
6. When comparing comps to the subject, note differences in sqft, condition (renovated vs original), and price per sqft.
7. Be warm, professional, and encouraging. Keep responses concise - 3-4 short paragraphs max.
8. Focus on MARKET DATA. Reference "recent sales" not "our model."
9. When asked about improvements, provide guidance but defer to the agent for ROI advice.
10. End with a brief follow-up question or encourage contacting their agent.

You have FULL ACCESS to the comparable sales data listed above. When sellers ask about comps, give them specific examples with addresses and prices. This builds trust and shows the depth of the analysis their agent has prepared.`;
  }
  
  // Get suggested questions based on conversation stage
  getSuggestedQuestions(): string[] {
    if (!this.cmaData) {
      return [
        "How is my home's value calculated?",
        "What factors affect my property value?",
        "How accurate are AI valuations?",
        "When is the best time to sell?"
      ];
    }
    
    const conversationLength = this.conversationHistory.length;
    
    if (conversationLength < 2) {
      // Initial questions
      return [
        "How did you calculate my home's value?",
        "What comparable properties did you analyze?",
        "Why is my home worth this amount?",
        "How confident are you in this valuation?"
      ];
    } else if (conversationLength < 6) {
      // Mid-conversation questions
      return [
        "How does my home compare to others in the area?",
        "What improvements could increase my value?",
        "Is this a good time to sell?",
        "How long might it take to sell?"
      ];
    } else {
      // Deeper questions
      return [
        "What's my best pricing strategy?",
        "Should I make repairs before listing?",
        "How do I maximize my sale price?",
        "What are the next steps to list?"
      ];
    }
  }
  
  // Check if the message is asking about specific comparable properties
  isAskingAboutComparables(message: string): boolean {
    const comparableKeywords = [
      'comparable', 'comps', 'similar', 'other homes', 'other properties',
      'compared to', 'comparison', 'nearby sales', 'recent sales'
    ];
    
    const lowerMessage = message.toLowerCase();
    return comparableKeywords.some(keyword => lowerMessage.includes(keyword));
  }
  
  // Format comparable properties for display
  formatComparables(comparables: any[]): string {
    if (!comparables || comparables.length === 0) {
      return "I don't have detailed comparable data available at the moment.";
    }
    
    const topComps = comparables.slice(0, 5);
    let response = `Here are ${topComps.length} comparable properties I analyzed:\n\n`;
    
    topComps.forEach((comp, index) => {
      response += `${index + 1}. ${comp.address || 'Property ' + (index + 1)}\n`;
      response += `   • Sold for: $${comp.close_price?.toLocaleString() || 'N/A'}\n`;
      response += `   • Size: ${comp.living_area?.toLocaleString() || 'N/A'} sqft\n`;
      response += `   • Beds/Baths: ${comp.bedrooms || '?'}/${comp.bathrooms || '?'}\n`;
      if (comp.close_date) {
        response += `   • Sale Date: ${new Date(comp.close_date).toLocaleDateString()}\n`;
      }
      response += '\n';
    });
    
    return response;
  }
}

export default SellerCMAConsultativeService;